import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { MSAL_INSTANCE } from '@azure/msal-angular';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import type { IPublicClientApplication } from '@azure/msal-browser';
import { EMPTY, from, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { isAuthBypassed } from '../auth/auth-bypass';
import { isApiUrl } from '../http/api-url';
import { rememberReturnUrl } from '../auth/return-url';

/** Error code of the 401 this interceptor answers by itself for a call made while nobody is
 *  signed in (WI 3861) — never a BE code. */
export const NOT_SIGNED_IN = 'NOT_SIGNED_IN';

/** MSAL clients that already started the re-authentication redirect: a page load fires several BE
 *  calls in parallel and only the first may start tearing the page down. A set keyed by client (the
 *  app has one) rather than a module flag, so each test starts clean. */
const reauthStarted = new WeakSet<object>();

// Local dev identities (used only while isAuthBypassed()). The BE resolves the real role server-side from the
// token's email — property_group_member / platform_admin — so the email here is what decides
// which surfaces you see. Switch from the browser console, no code edit needed:
//
//   localStorage.setItem('ext.devUser', 'agency'); location.reload();
//
// 'hotel' stays the default, so nothing changes unless you opt in. Note that the seeded agency
// user is ALSO a platform admin, so GET /me returns ADMIN for it with AGENCY_MANAGER among its
// availableRoles — pick Agency from the account menu's dev-only "View as" switcher (or preset
// sessionStorage 'ext.activeRole' to 'AGENCY_MANAGER') to land on the agency surfaces.
//
// Any other member (Feature 4123: a member per profile, e.g. a Front desk or Operations member
// seeded in the local DB) is reached by overriding the e-mail of the picked identity:
//
//   localStorage.setItem('ext.devEmail', 'frontdesk@example.local'); location.reload();
//   localStorage.removeItem('ext.devEmail'); location.reload();   // back to the picked identity
//
// The role claim stays the picked identity's one: the BE resolves the real role and profiles from
// the e-mail, so pick 'agency' for an agency member only to keep the claim consistent.
const DEV_USER_KEY = 'ext.devUser';
const DEV_EMAIL_KEY = 'ext.devEmail';
const DEV_IDENTITIES: Record<string, { email: string; role: string; sub: string }> = {
  // Legacy hotel_account seed — HOTEL_MANAGER, the historical default of this file.
  hotel: { email: 'manager@romapalace.local', role: 'HOTEL_MANAGER', sub: 'hotel-manager-local' },
  // Changeset 0030 seed — AGENCY_MANAGER of "Test Root Agency (dev seed)", the only agency group.
  agency: { email: 'amrk44778@gmail.com', role: 'AGENCY_MANAGER', sub: 'agency-local' },
};

/** Unsigned `alg: none` JWT — accepted only by the local BE profile, which doesn't verify
 *  signatures (quarkus.oidc.tenant-enabled: false there). */
function devJwt(): string {
  let pick: string | null = null;
  let emailOverride: string | null = null;
  try {
    pick = localStorage.getItem(DEV_USER_KEY);
    emailOverride = localStorage.getItem(DEV_EMAIL_KEY)?.trim() || null;
  } catch { /* private mode */ }
  const id = DEV_IDENTITIES[pick ?? ''] ?? DEV_IDENTITIES['hotel'];
  const email = emailOverride ?? id.email;
  const header = 'eyJhbGciOiJub25lIiwidHlwIjoiSldUIn0';   // {"alg":"none","typ":"JWT"}
  const payload = btoa(JSON.stringify({ emails: [email], extension_role: id.role, sub: id.sub }))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  return `Bearer ${header}.${payload}.fakesig`;
}

/** Attaches an Authorization header to the extranet BE's calls (isApiUrl: the API host and the paths below it only).
 *  Two modes, chosen by isAuthBypassed() — the same predicate the route guards use, so the app can
 *  never be in the state where one half skips the login and the other still demands it (WI #1345):
 *  - bypassed (local convenience): the fake unsigned dev JWT built above, whose identity is
 *    switchable from the console (see DEV_IDENTITIES).
 *  - not bypassed (dev-cloud/prod): the real access token of the extranet's Entra External ID tenant
 *    (AB#4431; Azure AD B2C before) for the signed-in MSAL account, scoped to environment.apiScopes.
 *    be-extranet-service verifies its signature, issuer and audience on every request that carries
 *    one.
 *
 *  The app talks to no other backend: the currency list now comes from the Extranet BE too
 *  (WI 3534), so no request leaves the browser for be-agency-service.
 *
 *  A missing or expired sign-in never sends a tokenless call any more (WI 3861). APIM validates the
 *  JWT before its CORS policy runs, so such a call can only come back as a 401 the browser reports
 *  as an opaque CORS error — the clue in the bug report — while the page it served stayed blank or
 *  fell on "Access denied":
 *  - nobody signed in (the guards are already redirecting to B2C): answered here with a 401
 *    `NOT_SIGNED_IN`, no round trip;
 *  - the signed-in account's session is over (`InteractionRequiredAuthError`: the 24 h B2C refresh
 *    token expired, ...): re-authenticate by redirect, once, remembering the page so the user
 *    comes back to it, and complete the call without issuing it (mirrors fe-hotel-b2b-web's fix of
 *    2026-08-30);
 *  - any other acquisition failure (network blip, MSAL hiccup) keeps the old tokenless fallback: a
 *    transient failure must not start a login redirect under the user. */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Skip anything that isn't our backend — notably presigned PUT requests (Azure Blob Storage,
  // MinIO locally), which carry their auth in the query string.
  const isBackendCall = isApiUrl(req.url);
  if (!isBackendCall) return next(req);

  if (!isAuthBypassed()) {
    const msal = inject(MSAL_INSTANCE) as unknown as IPublicClientApplication;
    if (!environment.apiScopes.length) return next(req);
    const account = msal.getActiveAccount();
    if (!account) {
      return throwError(() => new HttpErrorResponse({
        status: 401, statusText: 'Unauthorized', url: req.url, error: { error: NOT_SIGNED_IN },
      }));
    }
    const router = inject(Router);
    return from(msal.acquireTokenSilent({ scopes: environment.apiScopes, account })).pipe(
      // catchError sits BEFORE switchMap on purpose: it must only catch a token-acquisition
      // failure, not swallow HTTP errors from the actual request below (mirrors
      // fe-hotel-b2b-web's auth.interceptor.ts, same guard — a past bug there had this masking
      // real BE errors behind a silent unauthenticated retry).
      catchError((error: unknown) => {
        if (error instanceof InteractionRequiredAuthError) {
          if (!reauthStarted.has(msal)) {
            reauthStarted.add(msal);
            rememberReturnUrl(currentAppUrl(router));
            msal.acquireTokenRedirect({ scopes: environment.apiScopes, account })
              .catch(err => console.error('[Auth] re-authentication redirect failed to start:', err));
          }
          return EMPTY;
        }
        return of(null);
      }),
      switchMap(result => next(result
        ? req.clone({ setHeaders: { Authorization: `Bearer ${result.accessToken}` } })
        : req)),
    );
  }

  return next(req.clone({ setHeaders: { Authorization: devJwt() } }));
};

/** The page the user is on or heading to: the target of a navigation in flight (a guard's GET /me
 *  runs before the address bar changes), else the address bar (a call fired outside a navigation). */
function currentAppUrl(router: Router): string {
  const navigation = router.getCurrentNavigation();
  const target = navigation?.finalUrl ?? navigation?.extractedUrl;
  if (target) return router.serializeUrl(target);
  return `${globalThis.location?.pathname ?? ''}${globalThis.location?.search ?? ''}${globalThis.location?.hash ?? ''}`;
}
